import { Component, EventEmitter, inject, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { NgZorroModule } from "../../../../../../shared/modules/ng-zorro/ng-zorro.module";
import { ButtonComponent } from "@ppl/ui";
import { DatepickerComponent } from '@ppl/ui';
import { UploadComponent } from '@ppl/ui';
import { UserService } from '../../../../../../services/user.service';
import { TimesheetService } from '../../../../../../services/timesheet.service';
import { InvoiceService } from '../../../../../../services/invoice.service';
import { DateTimeFormatterService } from '../../../../../../shared/utils/date-time-formatter.service';
import { NotificationService } from '../../../../../../shared/services/notification.service';
import { DialogConfig, DialogService } from '../../../../../../shared/services/dialog.service';
import { TimesheetStaffDeniedReasonsComponent } from "../../../../timesheets/timesheet-staff-deniedreasons/timesheet-staff-deniedreasons.component";
import { LookupService } from '../../../../../../services/lookup.service';
import { ListService } from '../../../../../../shared/services/list.service';
import { InvoiceStaffErrorcodeComponent } from '../../../staff/invoices-staff-list/invoice-staff-errorcode/invoice-staff-errorcode.component';
import { ErrorAlertService } from '../../../../../../shared/services/error-alert.service';
import { NumberOnlyDirective } from '../../../../../../shared/directives/number-only.directive';
import { SmartyService } from '../../../../../../shared/services/smarty.service';
import { TimesheetStaffAuthDataComponent } from '../../../../timesheets/timesheet-staff-authdata/timesheet-staff-authdata.component';
import { SectionsService } from '../../../../../../services/sections.service';
import { AuthorizationDetailsComponent } from '../../../../authorizations/authorization-details/authorization-details.component';
import { AuditHistoryComponent } from '../../../../../../shared/modals/audit-history/audit-history.component';
import { InvoiceEditHistoryViewComponent } from '../../../staff/invoices-staff-list/invoice-edit-history-view/invoice-edit-history-view.component';
import { SharedDataService } from '../../../../../../shared/services/shared-data-service';
import { UploadHandlerService } from '../../../../../../shared/services/upload-handler.service';
import { ConsumerRateService } from '../../../../../../services/consumer-rater.service.';
import { environment } from '../../../../../../../environments/environment';

@Component({
  selector: 'ppl-add-invoice',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgZorroModule, ButtonComponent,
    DatepickerComponent, UploadComponent, TimesheetStaffDeniedReasonsComponent, InvoiceStaffErrorcodeComponent,
    NumberOnlyDirective, TimesheetStaffAuthDataComponent, AuthorizationDetailsComponent, AuditHistoryComponent,
    InvoiceEditHistoryViewComponent
  ],
  templateUrl: './add-invoice.component.html',
  styleUrl: './add-invoice.component.css',
})
export class AddInvoiceComponent implements OnInit {
  // Invoice_Upload_Doc_Type document types that are mandatory on every invoice submission.
  private static readonly REQUIRED_DOC_TYPES = ['Invoice Receipt', 'Supporting Document'];
  private dialogSvc = inject(DialogService);
  @ViewChild('denyDialog') denyDialog!: TemplateRef<any>;
  @ViewChild('deniedDialog') deniedDialog!: TemplateRef<any>;
  @ViewChild('pendedDialog') pendedDialog!: TemplateRef<any>;
  @ViewChild('authDialog') authDialog!: TemplateRef<any>;
  // Passed to ppl-authorization-details as an input; never put into the shared selected lead.
  selectedAuthorizationId: any = null;
  @ViewChild('authUsageDataDialog') authUsageDataDialog!: TemplateRef<any>;
  @ViewChild('invoiceEditHistoryDialog') invoiceEditHistoryDialog!: TemplateRef<any>;
  @ViewChild('invoiceAuditHistoryDialog') invoiceAuditHistoryDialog!: TemplateRef<any>;
  @Input() invoiceId!: any;
  @Input() providerId!: number;
  @Input() consumerId!: number;
  @Input() provider: any;
  @Input() consumers: any[] = [];
  @Input() providerView: boolean = false;
  @Output() invoiceSaved = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<any>();
  @Output() invoiceUpdated = new EventEmitter<boolean>();
  @Input() mode: 'add' | 'edit' | 'view' | 'actions' = 'add';
  @Input() staffView = false;
  isExternal = false;
  @Input() isReadOnly = false;
  @Input() canApproveReject = true;
  selectedConsumer: any;
  programName: string = '';
  invoiceData: any;
  pendedDialogId: string = '';
  invoiceForm: FormGroup;
  showEntries: boolean = false;
  fieldErrors: Record<number, Record<string, boolean>> = {};
  serviceCodes: { [index: string]: string[] } = {};
  customErrors: string[] = [];
  isFormSubmitReequested: boolean = false;
  uploadedFiles: any[] = [];
  // Document types offered in the receipt attachment dropdown.
  invoiceDocTypes: any[] = [];
  invoiceMaxEndDate: Date = new Date();
  maxEndDate: Date = new Date();
  attachments: any[] = [];
  isDisabled: boolean = false;
  approvedProcessingStatuses: string[] = ['Paid', 'Ready for Payment'];
  rejectedProcessingStatuses: string[] = ['Consumer Rejected'];
  denyReasons: any[] = [];
  listOfOption: string[] = [];
  selectedReason: string = '';
  deniedData: any[] = [];
  dispositionReason: any[] = [];
  /** Reason domain used by the Deny disposition in ppl-invoice-staff-errorcode. */
  invoiceDenialReasons: any[] = [];
  timesheetDisposition: any[] = [];
  errorcodeData: any[] = [];
  showInvoiceForm = true;
  canUpdateEntries: boolean = false;
  hasMilageEntries: boolean = false;
  authDetails: any;
  authUsageData: any;
  areSubscriptionsInitialized: boolean = false;
  // Tracks entries whose valueChanges have already been wired, to avoid double-subscribing.
  private subscribedEntries = new WeakSet<FormGroup>();
  selectedInvoiceLineId?: any;
  canEditInvoice: boolean = false;
  canApproveOrReject: boolean = false;

  constructor(private fb: FormBuilder,
    private userService: UserService,
    private timesheetService: TimesheetService,
    private invoiceService: InvoiceService,
    private dateFormatService: DateTimeFormatterService,
    private notificationService: NotificationService,
    private lookupService: LookupService,
    private errorAlertService: ErrorAlertService,
    private listService: ListService,
    private smartyService: SmartyService,
    private sectionService: SectionsService,
    private sharedDataService: SharedDataService,
    private uploadHandler: UploadHandlerService,
    private consumerRateService: ConsumerRateService) {
    this.invoiceForm = this.fb.group({
      providerId: [this.providerId],
      invoiceId: [null],
      consumer: [null, [Validators.required]],
      invoiceDate: [null, [Validators.required]],
      invoiceNumber: [null],
      invoiceEntries: this.fb.array([]),
      providerPplId: [this.provider?.providerPplId || ''],
      // Attestation the provider must tick before a new invoice can be submitted.
      attestation: [false],
    });
  }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.isExternal = environment.isExternal;
    this.invoiceDocTypes = this.lookupService.getLookupValue('Invoice_Upload_Doc_Type');

    if (this.mode === 'view' || this.mode === 'actions') {
      this.isDisabled = true;
      if (this.providerId) {
        this.setConsumersByProviderId();
      }
    }
    // Edit needs the consumer's associationType to decide whether the mileage rate is editable.
    if (this.mode === 'edit' && this.providerId && !this.consumers?.length) {
      this.setConsumersByProviderId();
    }
    if (this.mode === 'add') {
      this.canUpdateEntries = true;
      // Only new invoices require the accuracy attestation.
      const attestation = this.invoiceForm.get('attestation');
      attestation?.setValidators([Validators.requiredTrue]);
      attestation?.updateValueAndValidity();
    }

    if (this.mode !== 'add') {
      this.populateInvoiceData();
    }

    if (this.staffView && this.mode === 'add') {
      this.showInvoiceForm = false;
    }

    this.invoiceForm.get('consumer')!.valueChanges.subscribe(value => {
      if (value && this.staffView && this.mode === 'add') {
        this.showInvoiceForm = true;
      }
    });

    this.dispositionReason = this.lookupService.getLookupValue('Disposition Reason');
    this.invoiceDenialReasons = this.lookupService.getLookupValue('invoice_denial_reasons');
    this.timesheetDisposition = this.lookupService.getLookupValue('TimeSheet Disposition');
    this.denyReasons = this.lookupService.getLookupValue(
      'consumer_invoice_denial'
    );
    this.listOfOption = this.denyReasons.map((item: any) => item.lkpValueName);
  }

  setConsumersByProviderId() {
    const payload = {
      programCode: this.programName,
      providerId: this.providerId,
      providerPplId: null,
      consumerId: null,
      consumerPplId: null,
    };
    this.timesheetService.getConsumerListByPPLId(payload).subscribe((res: any) => {
      this.consumers = res.responsedata?.consumers.filter((c: any) => c.associationStatus === 'Association Active' || c.associationStatus === 'Registration Active' || c.associationStatus === 'Association Active with Gap' || c.associationStatus === 'Paperwork Complete');
      if (!this.provider) {
        const provider = res?.responsedata?.providers?.length > 0 ? res?.responsedata?.providers[0] : null;
        this.provider = provider;
      }

      if (this.consumerId) {
        this.selectedConsumer = this.consumers.find(c => c.consumerId === this.consumerId);
      }
    });
  }

  getConsumers() {
    const pplId = this.invoiceForm.get('providerPplId')?.value.trim();
    const payload = {
      programCode: this.programName,
      providerId: null,
      providerPplId: pplId,
      consumerId: null,
      consumerPplId: null,
    };
    this.timesheetService
      .getConsumerListByPPLId(payload)
      .subscribe((data: any) => {
        this.consumers = data?.responsedata?.consumers ?? [];
        const provider = data?.responsedata?.providers?.length > 0 ? data?.responsedata?.providers[0] : null;
        this.providerId = provider?.providerId || null;
        this.invoiceForm.get('providerId')?.patchValue(this.providerId);
        if (!this.consumers || this.consumers.length === 0) {
          this.invoiceForm.get('consumer')?.patchValue(null);
          // set error for consumer form control.
          this.invoiceForm.get('consumer')?.setErrors({ noConsumers: true });
        }
        else {
          // clear only consumer form control noconsumers error.
          this.invoiceForm.get('consumer')?.setErrors(null);
        }
      });
  }

  populateInvoiceData() {
    if (!this.invoiceId) {
      return;
    }

    this.invoiceService.getInvoice(this.invoiceId, this.programName).subscribe((data: any) => {
      this.invoiceData = data.responsedata;
      this.invoiceData.invoiceLine = this.invoiceData.invoiceLine.sort((line1: any, line2: any) => line1.id - line2.id);
      this.attachments = this.invoiceData?.attachmentList.map((att: any) => ({
        id: att.invoiceAttachmentID,
        name: att.fileName,
        // Attachments saved before the document type dropdown was introduced carry no type.
        type: this.getDocTypeLabel(att.otherType ?? att.documentType) || 'Invoice Receipt',
        docType: att.otherType ?? att.documentType
      }));
      this.uploadedFiles = this.attachments;

      this.patchInvoiceForm();
      let dateRangeSet = new Set<Date>();
      this.invoiceData.invoiceLine.forEach((line: any) => {
        dateRangeSet.add(this.dateFormatService.stringToDate(line.serviceDate));
      });

      let dateRangeArray = Array.from(dateRangeSet);
      let minDate = new Date(Math.min.apply(null, dateRangeArray as any));
      let maxDate = new Date(Math.max.apply(null, dateRangeArray as any));
      this.getServiceCode(minDate, maxDate);
      this.showEntries = true;
      this.hasMilageEntries = this.invoiceData.invoiceLine.some((il: any) => this.isMileageServiceCode(il.serviceCodeLkp));
      this.canUpdateEntries = (this.staffView || this.providerView) && this.invoiceData.invoiceLine.some((line: any) => line.processStatusLkp === 'Pended' || line.processStatusLkp === 'Saved');
      const canMoveBack = this.staffView && this.invoiceData.invoiceLine.some((line: any) => line.processStatusLkp === 'Pended' || line.processStatusLkp === 'Awaiting Approval' || line.processStatusLkp === 'Ready for Payment');
      const canCancel = (this.staffView || this.providerView) && this.invoiceData.invoiceLine.some((line: any) => line.processStatusLkp === 'Saved' || line.processStatusLkp === 'Awaiting Approval' || line.processStatusLkp === 'Ready for Payment');
      // we need to check if user can cancel the items, 
      this.canEditInvoice = canMoveBack || this.canUpdateEntries || (canCancel && this.invoiceData.invoiceLine.length > 1);
      this.canApproveOrReject = this.invoiceData.invoiceLine.some((line: any) => line.processStatusLkp === 'Awaiting Approval')
    });
  }

  // Service codes that are treated as mileage entries (rate fetched from API, from/to address required).
  private readonly mileageServiceCodes = ['338', '338-INV'];
  // Fallback mileage rate used when the rate API request fails or returns no usable value.
  private readonly MILEAGE_RATE_FALLBACK = 0.725;

  // Independent Provider associations get the mileage rate from the API; VE and IC enter it themselves.
  private readonly independentProviderAssociationTypes = ['IR', 'IP'];

  isMileageServiceCode(code: any): boolean {
    return this.mileageServiceCodes.includes(code);
  }

  // associationType of the selected participant, from getConsumers / getTimeSheetProviderConsumers.
  private getSelectedAssociationType(): string {
    const consumerId = this.invoiceForm.get('consumer')?.value;
    const consumer = this.consumers?.find((c: any) => c.consumerId === consumerId);
    return (consumer?.associationType ?? '').trim().toUpperCase();
  }

  // True when the selected participant's association is an Independent Provider (IR / IP).
  isIndependentProvider(): boolean {
    return this.independentProviderAssociationTypes.includes(this.getSelectedAssociationType());
  }

  // Rate is read-only only for mileage entries whose rate comes from the API (IR / IP).
  isRateEditable(invoiceEntry: AbstractControl): boolean {
    return !invoiceEntry.get('isMilageEntry')?.value || !this.isIndependentProvider();
  }

  // Fetch the latest mileage rate for the given service code and the entry's service date.
  // Falls back to MILEAGE_RATE_FALLBACK when the date is missing or the API fails/returns nothing.
  private fetchMileageRate(invoiceEntry: FormGroup, serviceCodeDetailId: any): void {
    const rateCtrl = invoiceEntry.get('rate');
    rateCtrl?.setValue(this.MILEAGE_RATE_FALLBACK);
    const serviceDate = invoiceEntry.get('serviceDate')?.value;
    if (!serviceDate) {
      return;
    }
    // yyyy-MM-dd so the date isn't sent as an encoded MM/dd/yyyy (e.g. 07%2F01%2F2026) in the URL path.
    const dateStr = this.dateFormatService.formatToString(serviceDate);
    this.consumerRateService.getServiceCodeRate(this.programName, serviceCodeDetailId, dateStr).subscribe({
      next: (data: any) => {
        const rate = Number(data?.responsedata);
        rateCtrl?.setValue(Number.isFinite(rate) && rate > 0 ? rate : this.MILEAGE_RATE_FALLBACK);
      },
      error: () => rateCtrl?.setValue(this.MILEAGE_RATE_FALLBACK)
    });
  }

  patchInvoiceForm() {
    this.invoiceEntries.clear();
    const invoiceDate = this.dateFormatService.formatStringToDate(this.invoiceData.invoiceDate);
    const today = new Date();
    this.maxEndDate = invoiceDate && invoiceDate < today ? invoiceDate : today;
    this.invoiceForm.patchValue({
      invoiceId: this.invoiceData.invoiceheaderID,
      consumer: this.invoiceData.consumer.consumerId,
      invoiceDate,
      invoiceNumber: this.invoiceData.invoiceNumber,
    });

    //find min and max service dates from invoice lines
    this.invoiceData.invoiceLine.forEach((line: any) => {
      const entryGroup = this.fb.group({
        selected: [false],
        id: [line.invoiceLineId],
        serviceDate: [this.dateFormatService.stringToDate(line.serviceDate), [Validators.required]],
        serviceCode: [line.serviceCodeDetailId, [Validators.required]],
        serviceDescription: [line.serviceCodeLkp, [Validators.required]],
        authIds: [line.authIds, [Validators.required]],
        serviceDetailId: [line.serviceDetailId, [this.serviceDetailRequired]],
        // Fallback label details from the invoice line, used to render the service code /
        // description / service detail when the auth is exhausted and getServiceCodesByConsumer omits it.
        planServiceCode: [line.planServiceCode],
        serviceCodeDescription: [line.serviceCodeDescription],
        serviceDetail: [line.serviceDetail],
        taskDescription: [line.taskDescription],
        isMilageEntry: [this.isMileageServiceCode(line.serviceCodeLkp)],
        serviceUnits: [line.billedUnits, [Validators.required, Validators.pattern(/^\d+(\.\d{1,3})?$/)]],
        rate: [line.unitPrice, [Validators.required]],
        totalCost: [+line.totalCost, [Validators.required, Validators.min(0.01)]],
        businessStatusLkp: [line.businessStatusLkp],
        processStatusLkp: [line.processStatusLkp],
        checkNo: [line.checkNo],
        createdDate: [this.dateFormatService.formatDate(line.createdDate, 'MM/dd/yyyy')],
        mcoName: [line.mcoName],
        consumerDecisionBy: [line.consumerDecisionBy],
        consumerDecisionDate: [line.consumerDecisionDate ? this.dateFormatService.formatDateTimeNoSeconds(line.consumerDecisionDate) : null],
        canCancel: [line.processStatusLkp === 'Saved' || line.processStatusLkp === 'Awaiting Approval' || line.processStatusLkp === 'Ready for Payment'],
        invoiceComments: [line.invoiceComments]
      });
      this.invoiceEntries.push(entryGroup);
    });

    if (this.mode !== 'add') {
      this.invoiceForm.disable();
      this.invoiceEntries.disable();
      this.invoiceEntries.controls.forEach((entry) => {
        // if either of the statuses is 'Awaiting Approval', enable the checkbox.
        const canApproveOrReject = entry.get('processStatusLkp')?.value === 'Awaiting Approval' || entry.get('businessStatusLkp')?.value === 'Awaiting Approval';
        const canSelectForProcessing = entry.get('processStatusLkp')?.value === 'Pended' || entry.get('processStatusLkp')?.value === 'Submitted' || entry.get('processStatusLkp')?.value === 'System Error';
        const canMoveBack = this.staffView && (entry.get('processStatusLkp')?.value === 'Pended' || entry.get('processStatusLkp')?.value === 'Awaiting Approval' || entry.get('processStatusLkp')?.value === 'Ready for Payment');
        // internal staff can resubmit denied lines for processing
        const canReprocessDenied = this.canReprocessDenied && this.isDeniedLine(entry);
        if (canApproveOrReject || canSelectForProcessing || canMoveBack || canReprocessDenied) {
          entry.get('selected')?.enable();
        }
        else {
          entry.get('selected')?.disable();
        }

        // if invoice entry is pended, enable all fields for editing only for staff and providers
        if ((entry.get('processStatusLkp')?.value === 'Pended' || entry.get('processStatusLkp')?.value === 'Saved') && (this.staffView || this.providerView)) {
          entry.enable();
        }
      });
    }
  }

  getServiceCode(fromDate: any, toDate: any) {
    const formattedStartDate = this.dateFormatService.formatDateToString(fromDate);
    const formattedEndDate = this.dateFormatService.formatDateToString(toDate);

    if (this.serviceCodes[formattedStartDate] && this.serviceCodes[formattedStartDate].length > 0) {
      return;
    }

    const payload = {
      programCode: this.programName,
      consumerId: this.invoiceForm.getRawValue().consumer,
      periodFromDate: formattedStartDate,
      periodToDate: formattedEndDate,
      source: 'invoice',
      providerId: this.providerId ?? this.provider?.providerId
    };

    this.timesheetService
      .getServiceCodesByConsumer(payload)
      .subscribe((data: any) => {
        if (data.responsedata == "Auth Fail" && data.status == 404) {
          this.showEntries = false;
          this.customErrors = data.responsemsg.split(",");
          return;
        }

        this.processServiceCodes(data.responsedata);
        if (!this.areSubscriptionsInitialized) {
          this.invoiceEntries.controls.forEach((element) => {
            this.subscribeToInvoiceEntriesChanges(element as FormGroup);
          });

          // this we are using for created invoices only. for the first time
          this.areSubscriptionsInitialized = true;
        }
      });
  }

  getTotalInvoiceAmount(): number {
    let total = 0;
    this.invoiceEntries.controls.forEach((entry) => {
      const totalCost = entry.get('totalCost')?.value;
      if (totalCost) {
        total += parseFloat(totalCost);
      }
    });
    return total;
  }

  getTotalInvoicePaid(): number {
    let total = 0;
    this.invoiceEntries.controls.forEach((entry) => {
      const processStatusLkp = entry.get('processStatusLkp')?.value;
      const totalCost = entry.get('totalCost')?.value;
      if (processStatusLkp === 'Paid' && totalCost) {
        total += parseFloat(totalCost);
      }
    });
    return total;
  }

  addNewLine() {
    this.invoiceForm.markAllAsTouched();
    if (this.invoiceForm.get('consumer')?.invalid || this.invoiceForm.get('invoiceDate')?.invalid) {
      return;
    }

    this.addEntry();
  }

  addEntry() {
    const invoiceEntry = this.fb.group({
      selected: [false],
      id: [null],
      serviceDate: [null, [Validators.required]],
      serviceCode: [null, [Validators.required]],
      serviceDescription: [null, [Validators.required]],
      authIds: [null, [Validators.required]],
      serviceDetailId: [null, [this.serviceDetailRequired]],
      isMilageEntry: [false],
      serviceUnits: [null, [Validators.required, Validators.pattern(/^\d+(\.\d{1,3})?$/)]],
      rate: [null, [Validators.required]],
      totalCost: [0, [Validators.required, Validators.min(0.01)]],
      businessStatusLkp: [null],
      processStatusLkp: [null],
      canCancel: [true],
      invoiceComments: [null]
    });
    this.invoiceEntries.push(invoiceEntry);
    this.showEntries = true;
    this.subscribeToInvoiceEntriesChanges(invoiceEntry);
  }

  subscribeToInvoiceEntriesChanges(invoiceEntry: FormGroup) {
    // Guard against double-subscribing the same entry (addEntry subscribes it, and the
    // getServiceCode loop may subscribe it again) which would fire valueChanges — and the
    // mileage rate API call — twice per selection.
    if (this.subscribedEntries.has(invoiceEntry)) {
      return;
    }
    this.subscribedEntries.add(invoiceEntry);

    invoiceEntry?.get('serviceDate')?.valueChanges.subscribe((value) => {
      invoiceEntry.get('serviceCode')?.setValue(null, { emitEvent: false });
      invoiceEntry.get('authIds')?.setValue(null, { emitEvent: false });
      invoiceEntry.get('serviceDetailId')?.setValue(null, { emitEvent: false });
    });

    // Service description (auth) drives which service details are available, so drop any
    // selection made against the previous auth.
    invoiceEntry?.get('authIds')?.valueChanges.subscribe(() => {
      invoiceEntry.get('serviceDetailId')?.setValue(null, { emitEvent: false });
    });

    invoiceEntry?.get('serviceCode')?.valueChanges.subscribe((value) => {
      const serviceCodeDetails = this.getServiceCodeForServiceDate(invoiceEntry.get('serviceDate')?.value);
      const selectedServiceCode = serviceCodeDetails.find((opt: any) => opt.serviceCodeDetailId === value);
      invoiceEntry.get('serviceDescription')?.setValue(selectedServiceCode?.serviceCodeLkp, { emitEvent: false });
      invoiceEntry.get('authIds')?.setValue(null, { emitEvent: false });
      invoiceEntry.get('serviceDetailId')?.setValue(null, { emitEvent: false });
      if (this.isMileageServiceCode(selectedServiceCode?.serviceCodeLkp)) {

        invoiceEntry.get('isMilageEntry')?.setValue(true)
        invoiceEntry.get('isMilageEntry')?.updateValueAndValidity();
        if (this.isIndependentProvider()) {
          // IR / IP: fetch the latest mileage rate for this service code detail + service date; fall back to 0.725.
          this.fetchMileageRate(invoiceEntry, selectedServiceCode?.serviceCodeDetailId);
        } else {
          // VE / IC: no rate lookup, the rate is entered manually.
          invoiceEntry.get('rate')?.setValue(null);
        }
      }
      else {
        invoiceEntry.get('isMilageEntry')?.setValue(false)
        invoiceEntry.get('isMilageEntry')?.updateValueAndValidity();
      }

      this.hasMilageEntries = this.invoiceEntries.value.some((v: any) => this.isMileageServiceCode(v.serviceDescription));
    });

    invoiceEntry.valueChanges.subscribe((value) => {
      const selectedFormGroupIndex = this.invoiceEntries.controls.indexOf(invoiceEntry);
      const serviceUnits = parseFloat(value.serviceUnits) || 0;
      const rate = parseFloat(value.rate) || 0;
      const totalCost = serviceUnits * rate;
      if (rate > 0 && serviceUnits > 0) {
        invoiceEntry.get('totalCost')?.setValue(totalCost.toFixed(3), { emitEvent: false });
      }
      this.fieldErrors[selectedFormGroupIndex] = {
        serviceDate: value.serviceDate ? false : true,
        serviceCode: value.serviceCode ? false : true,
        serviceDescription: value.serviceDescription ? false : true,
        serviceUnits: value.serviceUnits ? false : true,
        rate: value.rate > 0 ? false : true,
        totalCost: rate > 0 && serviceUnits > 0 ? false : true,
      };
    });
  }

  onSave() {
    this.isFormSubmitReequested = true;
    if (this.mode !== 'add') {
      this.onInvoiceUpdate();
    }
    else {
      this.invoiceEntries.markAllAsTouched();
      this.invoiceEntries.controls.forEach((entry, index) => {
        this.fieldErrors[index] = {};
        const serviceDate = entry.get('serviceDate')?.value;
        const serviceCode = entry.get('serviceCode')?.value;
        const authIds = entry.get('authIds')?.value;
        const serviceUnits = entry.get('serviceUnits')?.value;
        const rate = entry.get('rate')?.value;
        const totalCost = entry.get('totalCost')?.value;
        this.fieldErrors[index]['serviceDate'] = !serviceDate;
        this.fieldErrors[index]['serviceCode'] = !serviceCode;
        this.fieldErrors[index]['authIds'] = !authIds;
        // Only flag it when the row's auth actually has service details to pick from.
        this.fieldErrors[index]['serviceDetailId'] =
          this.hasServiceDetailOptions(entry) && !entry.get('serviceDetailId')?.value;
        this.fieldErrors[index]['serviceUnits'] = !serviceUnits || serviceUnits <= 0;
        this.fieldErrors[index]['rate'] = !rate || rate <= 0;
        this.fieldErrors[index]['totalCost'] = !totalCost || totalCost <= 0;
      });
      if (this.invoiceEntries.valid && this.missingRequiredDocTypes.length === 0 && this.isAttested) {
        const payload = this.generateSaveInvoicePayload();
        this.invoiceService.saveInvoice(payload, this.programName).subscribe((response: any) => {
          this.invoiceSaved.emit(response.responsedata);
        });
      }
    }
  }

  onInvoiceUpdate() {
    this.isFormSubmitReequested = true;

    const selectedItems = this.invoiceEntries.controls.filter(entry => entry.get('selected')?.value) as FormGroup[];
    const selectedIds = selectedItems.map(e => e.get('id')?.value);
    if (selectedIds.length === 0) {
      this.notificationService.error(
        'Error',
        `Please select at least one invoice line to submit.`
      );

      return;
    }

    if (selectedItems.some((e: any) => e.get('processStatusLkp')?.value !== 'Pended' && e.get('processStatusLkp')?.value !== 'Saved')) {
      this.notificationService.error(
        'Error',
        `Only invoice lines with status 'Pended' and 'Saved' can be submitted.`
      );
      return;
    }
    // enable validation for only pended entries.
    selectedItems.forEach((entry, index) => {
      this.fieldErrors[index] = {};
      const serviceDate = entry.get('serviceDate')?.value;
      const serviceCode = entry.get('serviceCode')?.value;
      const authIds = entry.get('authIds')?.value;
      const serviceUnits = entry.get('serviceUnits')?.value;
      const rate = entry.get('rate')?.value;
      const totalCost = entry.get('totalCost')?.value;
      this.fieldErrors[index]['serviceDate'] = !serviceDate;
      this.fieldErrors[index]['serviceCode'] = !serviceCode;
      this.fieldErrors[index]['authIds'] = !authIds;
      // Only flag it when the row's auth actually has service details to pick from.
      this.fieldErrors[index]['serviceDetailId'] =
        this.hasServiceDetailOptions(entry) && !entry.get('serviceDetailId')?.value;
      this.fieldErrors[index]['serviceUnits'] = !serviceUnits || serviceUnits <= 0;
      this.fieldErrors[index]['rate'] = !rate || rate <= 0;
      this.fieldErrors[index]['totalCost'] = !totalCost || totalCost <= 0;
    });

    if (selectedItems.length > 0 && selectedItems.every(entry => entry.valid) && this.missingRequiredDocTypes.length === 0) {
      const payload = this.generateUpdateInvoicePayload(selectedItems);
      this.invoiceService.saveInvoice(payload, this.programName).subscribe((response: any) => {
        this.invoiceSaved.emit(response.responsedata);
      });
    }
  }

  generateSaveInvoicePayload() {
    const fileIds = this.uploadedFiles.map(file => file.id).join(',');
    const formValue = this.invoiceForm.value;
    const consumer = this.consumers.find(c => c.consumerId === formValue.consumer);
    const payload: any = {
      action: 'submit',
      provider: {
        providerId: this.providerId,
        providerName: this.provider?.name || '',
        providerPplId: this.provider?.providerPplId || ''
      },
      consumer: {
        consumerId: formValue.consumer,
        consumerPplId: consumer?.consumerPplId || '',
        consumerName: consumer?.consumerFullName || ''
      },
      invoiceheaderID: null,
      invoiceDate: this.dateFormatService.formatDateToString(formValue.invoiceDate),
      invoiceNumber: formValue.invoiceNumber,
      invoiceLine: formValue.invoiceEntries.map((entry: any) => ({
        invoiceLineId: 0,
        serviceDate: this.dateFormatService.formatDateToString(entry.serviceDate),
        serviceCodeDetailId: entry.serviceCode,
        serviceCodeLkp: entry.serviceDescription,
        authIds: entry.authIds,
        serviceDetailId: entry.serviceDetailId ?? null,
        billedUnits: entry.serviceUnits,
        unitPrice: entry.rate,
        totalCost: +entry.totalCost
      })),
      attachments: fileIds,
      source: this.staffView ? 'StaffView' : null
    };
    return payload;
  }

  generateUpdateInvoicePayload(selectedItems: FormGroup[]) {
    const fileId = this.uploadedFiles.map(file => file.id).join(',');
    const formValue = this.invoiceForm.getRawValue();
    const consumer = this.consumers.find(c => c.consumerId === formValue.consumer);
    const payload: any = {
      action: 'submit',
      provider: {
        providerId: this.providerId,
        providerName: this.provider?.name || '',
        providerPplId: this.provider?.providerPplId || ''
      },
      consumer: {
        consumerId: formValue.consumer,
        consumerPplId: consumer?.consumerPplId || '',
        consumerName: consumer?.consumerFullName || ''
      },
      invoiceheaderID: this.invoiceData.invoiceheaderID,
      invoiceDate: this.dateFormatService.formatDateToString(formValue.invoiceDate),
      invoiceNumber: formValue.invoiceNumber,
      invoiceLine: selectedItems.map((entry: any) => ({
        invoiceLineId: entry.value.id,
        serviceDate: this.dateFormatService.formatDateToString(entry.value.serviceDate),
        serviceCodeDetailId: entry.value.serviceCode,
        serviceCodeLkp: entry.value.serviceDescription,
        authIds: entry.value.authIds,
        serviceDetailId: entry.value.serviceDetailId ?? null,
        billedUnits: entry.value.serviceUnits,
        unitPrice: entry.value.rate,
        totalCost: +entry.value.totalCost
      })),
      attachments: fileId
    };
    return payload;
  }

  onCancel() {
    this.cancel.emit({ isCloseTriggeredByDialog: true });
  }

  get invoiceEntries() {
    return this.invoiceForm.get('invoiceEntries') as FormArray;
  }

  // The accuracy attestation only gates new invoices; other modes are always considered attested.
  get isAttested(): boolean {
    return this.mode !== 'add' || this.invoiceForm.get('attestation')?.value === true;
  }

  isFieldEmpty(fieldName: string, index: number): boolean {
    const control = this.invoiceEntries.at(index);
    if (this.mode === 'edit' && (control?.get('processStatusLkp')?.value !== 'Pended' && control.get('businessStatusLkp')?.value !== 'Saved')) {
      return false;
    }

    return this.fieldErrors[index]?.[fieldName] ?? false;
  }

  isAddDisabled(entry: any): boolean {
    return true;
  }

  /**
   * Copy a line into a new one directly below it. Offered only where "Add a new line" is, so the
   * two stay in step — duplicating is just a pre-filled add.
   *
   * `id`, the statuses and `canCancel` are deliberately reset rather than copied: the duplicate is
   * a brand-new line, and carrying the source's id over would make the save update the original
   * instead of inserting.
   */
  duplicateRow(index: number): void {
    const entry = this.invoiceEntries.at(index) as FormGroup;

    const invoiceEntry = this.fb.group({
      selected: [false],
      id: [null],
      serviceDate: [entry.get('serviceDate')?.value, [Validators.required]],
      serviceCode: [entry.get('serviceCode')?.value, [Validators.required]],
      serviceDescription: [entry.get('serviceDescription')?.value, [Validators.required]],
      authIds: [entry.get('authIds')?.value, [Validators.required]],
      serviceDetailId: [entry.get('serviceDetailId')?.value, [this.serviceDetailRequired]],
      isMilageEntry: [entry.get('isMilageEntry')?.value],
      serviceUnits: [entry.get('serviceUnits')?.value, [Validators.required, Validators.pattern(/^\d+(\.\d{1,3})?$/)]],
      rate: [entry.get('rate')?.value, [Validators.required]],
      totalCost: [entry.get('totalCost')?.value, [Validators.required, Validators.min(0.01)]],
      businessStatusLkp: [null],
      processStatusLkp: [null],
      canCancel: [true]
    });

    this.invoiceEntries.insert(index + 1, invoiceEntry);
    this.showEntries = true;
    this.subscribeToInvoiceEntriesChanges(invoiceEntry);
  }

  /** Duplicate is available exactly where "Add a new line" is — add mode only. */
  get canDuplicateEntry(): boolean {
    return this.mode === 'add';
  }

  /**
   * Existing delete rule, lifted out of the template so the actions cell can ask for it without
   * repeating the expression: an added line, a blank-status line or a cancellable one, and never
   * the last remaining line.
   */
  canDeleteEntry(index: number): boolean {
    const entry = this.invoiceEntries.at(index);
    const isRemovable =
      this.mode === 'add'
      || entry?.get('processStatusLkp')?.value === ''
      || entry?.get('canCancel')?.value === true;

    return isRemovable && this.invoiceEntries.length > 1;
  }

  deleteRow(index: number): void {
    if (this.invoiceEntries.length > 1) {
      const currentElement = this.invoiceEntries.at(index);
      const invoiceLineId = currentElement.get('id')?.value;
      if (invoiceLineId === 0 || this.mode === 'add') {
        this.invoiceEntries.removeAt(index);
      }
      else {
        this.onInActivateInvoiceLines([currentElement]);
      }
    }
  }

  addNewRow(dateIndex: number): void {
    const entry = this.invoiceEntries.at(dateIndex) as FormGroup;
    const timeSlots = entry.get('timeSlots') as FormArray;
    const newTimeSlot = this.fb.group({
      serviceDate: [null],
    });

    timeSlots.push(newTimeSlot);
  }

  // ppl-upload emits the lookup code for a selected document type, or the free text typed
  // against the "Other" option. Resolve to the display label for the file list.
  private getDocTypeLabel(documentType: any): string {
    if (!documentType) return '';
    return this.lookupService.getLookupName(this.invoiceDocTypes, documentType) ?? String(documentType);
  }

  // Document types from Invoice_Upload_Doc_Type that must each have at least one attachment
  // before an invoice can be submitted.
  get missingRequiredDocTypes(): string[] {
    return AddInvoiceComponent.REQUIRED_DOC_TYPES.filter(
      (required) => !this.uploadedFiles.some((file) => this.isDocType(file, required))
    );
  }

  // Match on the resolved label or the raw lookup code, so attachments are recognised whichever
  // of the two the file carries.
  private isDocType(file: any, docTypeName: string): boolean {
    const required = docTypeName.toLowerCase();
    return [file?.type, file?.docType]
      .some((value) => value != null && String(value).toLowerCase() === required);
  }

  // Parent upload handler for ppl-upload — upload to backend via SharedService
  onFileSelected(event: any) {
    const file = event?.file as File;
    if (!file) return;
    const program = this.userService.getSelectedProgram()?.program_code ?? '';
    const type = this.programName || this.userService.getLeadEntry();
    const flowName = (type === 'authorizations') ? 'auth' : this.sharedDataService.getType(type);

    const formData = new FormData();
    formData.append('file', file as any);

    const requestPayload: any = {
      fileType: file.name.split('.').pop() || '',
      otherType: event.documentType || '',
      program,
      type: flowName,
      ...(flowName === 'auth' ? { authDetailId: event.leadId } : { leadId: event.leadId })
    };

    if ((event.consumerId && event.providerId) || (event.source === 'purchaserequest' && event.consumerId)) {
      Object.assign(requestPayload, {
        consumerId: event.consumerId,
        providerId: event.providerId,
        type: event.source || type
      });
    }

    formData.append('json', new Blob([JSON.stringify(requestPayload)], { type: 'application/json' }));

    this.uploadHandler.uploadFromEvent(event, type).subscribe({
      next: (response: any) => {
        const id = response?.responsedata?.file_upload_tracking_number ?? Date.now();
        this.uploadedFiles.push({
          id, name: file.name, size: file.size,
          type: this.getDocTypeLabel(event.documentType),
          docType: event.documentType
        });
      },
      error: () => this.notificationService.error('Error', `${file.name} file upload failed.`)
    });
  }

  onFileRemoved(fileId: any) {
    const id = fileId;
    const createType = this.invoiceId ? 'detail' : 'new';
    const docSource = 'invoice';
    this.uploadHandler.deleteFile(this.programName, id, createType, docSource).subscribe(() => {
      this.uploadedFiles = this.uploadedFiles.filter((f) => String(f.id) !== String(id));
    });
  }

  onDownloadRequested(file: any) {
    this.uploadHandler.downloadFile(file, 'invoice');
  }

  getServiceDescriptionsBasedOnServiceDate(serviceDate: Date, serviceCode: any): any[] {
    const serviceCodesForDate = this.getServiceCodeForServiceDate(serviceDate);
    const selectedServiceCode = serviceCodesForDate.find((opt: any) => opt.serviceCodeDetailId === serviceCode);
    if (selectedServiceCode?.authList.length === 0) {
      return [];
    }

    return selectedServiceCode?.authList || [];
  }

  // Service code dropdown options for a given entry. If the selected service code isn't among
  // the fetched active codes (e.g. the auth is exhausted so getServiceCodesByConsumer omits it),
  // inject a fallback option built from serviceCodeDescription on the invoice line so it renders.
  getServiceCodeOptions(entry: any): any[] {
    const options = [...this.getServiceCodeForServiceDate(entry?.get('serviceDate')?.value)];
    const selectedId = entry?.get('serviceCode')?.value;
    if (selectedId != null && !options.some((opt: any) => opt.serviceCodeDetailId === selectedId)) {
      options.push({
        serviceCodeDetailId: selectedId,
        // serviceCodeDescription is the label for the service code (serviceCodeLkp) column.
        serviceCodeDetail: entry?.get('serviceCodeDescription')?.value || '',
        authList: []
      });
    }
    return options;
  }

  // Service description dropdown options for a given entry, with a fallback option built from the
  // planServiceCode on the invoice line when the selected auth isn't in the fetched list.
  getServiceDescriptionOptions(entry: any): any[] {
    const options = [...this.getServiceDescriptionsBasedOnServiceDate(entry?.get('serviceDate')?.value, entry?.get('serviceCode')?.value)];
    const selectedIds = entry?.get('authIds')?.value;
    if (selectedIds != null && !options.some((opt: any) => opt.ids === selectedIds)) {
      options.push({
        ids: selectedIds,
        // planServiceCode is the label for the service description (authIds) column.
        desc: entry?.get('planServiceCode')?.value || ''
      });
    }
    return options;
  }

  // Service details dropdown options for a given entry - driven by the auth (service description)
  // selected on the entry. Returns [] when the auth carries no service details, in which case the
  // column renders nothing and the field isn't required.
  /**
   * Service Details only applies when the selected authorization actually exposes some. An auth
   * with none leaves the cell showing "-" and the field is not required.
   */
  hasServiceDetailOptions(entry: any): boolean {
    return this.getServiceDetailOptions(entry).length > 0;
  }

  /**
   * Required only while the parent row's authorization offers service details. Written as a
   * validator rather than a static Validators.required so it re-evaluates whenever the row's
   * serviceDetailId is reset — which the serviceDate / serviceCode / authIds subscriptions all do
   * via setValue, and setValue always re-runs validation regardless of emitEvent.
   */
  private serviceDetailRequired = (control: AbstractControl): ValidationErrors | null => {
    const entry = control.parent;
    if (!entry || !this.hasServiceDetailOptions(entry)) {
      return null;
    }
    return control.value == null || control.value === '' ? { required: true } : null;
  };

  getServiceDetailOptions(entry: any): any[] {
    const authOptions = this.getServiceDescriptionsBasedOnServiceDate(entry?.get('serviceDate')?.value, entry?.get('serviceCode')?.value);
    const selectedAuthIds = entry?.get('authIds')?.value;
    const selectedAuth = authOptions.find((opt: any) => opt.ids === selectedAuthIds);
    const options = [...(selectedAuth?.serviceDetails || [])];
    const selectedId = entry?.get('serviceDetailId')?.value;
    // Keep the saved selection visible when the auth isn't in the fetched list (e.g. exhausted auth).
    if (selectedId != null && !options.some((opt: any) => opt.authProviderServiceId === selectedId)) {
      options.push({
        authProviderServiceId: selectedId,
        serviceDetail: entry?.get('serviceDetail')?.value || ''
      });
    }
    return options;
  }

  onInvoiceDateChange(event: any) {
    const today = new Date();
    this.maxEndDate = event && event < today ? event : today;
  }

  onServiceDateChange(event: any) {
    this.getServiceCode(event, event);
  }

  getServiceCodeForServiceDate(serviceDate: Date): any[] {
    if (!serviceDate) {
      return [];
    }

    const codes = this.serviceCodes[this.dateFormatService.formatDateToString(serviceDate)] || [];
    return codes.sort((a: any, b: any) => (a.serviceCodeDetail || '').localeCompare(b.serviceCodeDetail || ''));
  }

  toggleSelectAll(event: any) {
    const isChecked = event.target.checked;
    this.invoiceEntries.controls.forEach((entry: any) => {
      if (!entry.get('selected')?.disabled) {
        entry.patchValue({ selected: isChecked });
      }
    });
  }

  approveSelected() {
    const selectedItems = this.invoiceEntries.controls.filter(entry => entry.get('selected')?.value);
    const selectedIds = selectedItems.map(e => e.get('id')?.value);
    if (selectedIds.length === 0) {
      this.notificationService.error(
        'Error',
        `Please select at least one invoice line to approve.`
      );

      return;
    }

    if (selectedItems.some((e: any) => e.get('processStatusLkp')?.value !== 'Awaiting Approval' && e.get('businessStatusLkp')?.value !== 'Awaiting Approval')) {
      this.notificationService.error(
        'Error',
        `Only invoice lines with status 'Awaiting Approval' can be approved.`
      );
      return;
    }

    const payload = {
      invoiceHeaderId: this.invoiceId,
      invoiceLineIds: selectedIds,
      action: "Approve",
      status: "GP",
      programCode: this.programName,
      participantId: this.invoiceForm.get('consumer')?.value,
      providerId: this.providerId,
      remarks: ""
    };
    this.invoiceService.updateInvoiceLinesStatus(payload, this.programName).subscribe((response: any) => {
      this.populateInvoiceData();
      this.invoiceUpdated.emit(true);
    });
  }

  denySelected() {
    const selectedItems = this.invoiceEntries.controls.filter(entry => entry.get('selected')?.value);
    const selectedIds = selectedItems.map(e => e.get('id')?.value);

    if (selectedIds.length === 0) {
      this.notificationService.error(
        'Error',
        `Please select at least one invoice line to deny.`
      );

      return;
    }

    if (selectedItems.some((e: any) => e.get('processStatusLkp')?.value !== 'Awaiting Approval' && e.get('businessStatusLkp')?.value !== 'Awaiting Approval')) {
      this.notificationService.error(
        'Error',
        `Only invoice lines with status 'Awaiting Approval' can be denied.`
      );
      return;
    }

    this.openDenyPopup(selectedIds);
  }

  openDenyPopup(selectedIds: number[]) {
    const dialogConfig: DialogConfig = {
      title: 'Deny Invoice',
      okText: 'Deny',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
    };

    this.dialogSvc.openDialog(
      this.denyDialog,
      dialogConfig,
      () => this.handleOk(selectedIds),
      () => this.handleDenyCancel()
    );
  }

  openPendedDialog(title: string) {
    const dialogConfig: DialogConfig = {
      title: title,
      okText: 'Ok',
      size: 'x-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.pendedDialogId = this.dialogSvc.openDialog(
      this.pendedDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  handleDenyCancel() {
    this.selectedReason = '';
  }

  handleOk(selectedIds: number[]) {
    const payload = {
      invoiceHeaderId: this.invoiceId,
      invoiceLineIds: selectedIds,
      action: "Reject",
      status: "CD",
      programCode: this.programName,
      participantId: this.invoiceForm.get('consumer')?.value,
      providerId: this.providerId,
      remarks: this.selectedReason
    };
    this.invoiceService.updateInvoiceLinesStatus(payload, this.programName).subscribe((response: any) => {
      this.populateInvoiceData();
      this.invoiceUpdated.emit(true);
    });
  }

  viewInvoiceEntryStatusDetails(index: number) {
    const invoiceLine = this.invoiceEntries.at(index).getRawValue();
    const invoiceLineId = invoiceLine?.id;

    const payload = this.buildRequestPayloadForStatusDetail(invoiceLineId);
    this.listService
      .getListData(payload, 'claims')
      .subscribe((data: any) => {
        if (invoiceLine.processStatusLkp === 'Pended') {
          this.errorcodeData = data.responsedata;
          this.openPendedDialog('Pended Invoice Line Details');
          return;
        }
        else if (invoiceLine.processStatusLkp === 'Denied' || invoiceLine.businessStatusLkp === 'Denied') {
          this.deniedData = data.responsedata;
          this.deniedData.forEach((e: any) => {
            // The Deny disposition saves its reason from the invoice_denial_reasons domain, while
            // every other disposition uses Disposition Reason. Resolving a deny reason against
            // Disposition Reason found no match and threw on `[0].lkpValueName`, which aborted this
            // callback before openDeniedDialog() below could run.
            if (e.reasonLkp) {
              e.reason = e.dispositionLkp === 'D'
                ? this.getLookupName(e.reasonLkp, this.invoiceDenialReasons, this.dispositionReason)
                : this.getLookupName(e.reasonLkp, this.dispositionReason, this.invoiceDenialReasons);
            }

            if (e.dispositionLkp) {
              e.disposition = this.getLookupName(e.dispositionLkp, this.timesheetDisposition);
            }
          });
          this.openDeniedDialog();
        }
      });
  }

  /**
   * Display name for a lookup code, checked against each domain in order. Falls back to the raw
   * code so an unmapped value shows as-is instead of breaking the caller.
   */
  private getLookupName(code: any, ...domains: any[][]): string {
    for (const domain of domains) {
      const match = domain?.find((item: any) => item.lkpValueCode == code);
      if (match) {
        return match.lkpValueName;
      }
    }
    return code;
  }

  openDeniedDialog() {
    const dialogConfig: DialogConfig = {
      title: `Denied Invoice Line Details`,
      okText: 'Ok',
      size: 'x-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.deniedDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  handleCancel() {
    this.selectedInvoiceLineId = null;
  }

  buildRequestPayloadForStatusDetail(invoiceLineId: any) {
    return {
      pageName: 'invoice-processCode',
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: 1,
      searchParam: [
        {
          jsonPath: 'invoiceLineId',
          value: invoiceLineId,
          operator: 'Equal',
        },
      ],
      orderByColumn: ['invoiceLineId'],
      orderBy: ['desc'],
    };
  }

  private processServiceCodes(serviceCodesData: any[]) {
    if (Array.isArray(serviceCodesData)) {
      serviceCodesData.forEach((serviceCode: any) => {
        if (Array.isArray(serviceCode.authList)) {
          serviceCode.authList.forEach((auth: any) => {
            const fromDate = this.dateFormatService.createDateInUTC(auth.fromDate);
            const toDate = this.dateFormatService.createDateInUTC(auth.toDate);
            for (
              let d = fromDate;
              d <= toDate;
              d.setDate(d.getDate() + 1)
            ) {
              const dateStr = this.dateFormatService.formatDateToString(d);
              if (!this.serviceCodes[dateStr]) {
                this.serviceCodes[dateStr] = [];
              }
              // Avoid duplicates
              if (!this.serviceCodes[dateStr].some((sc: any) => sc.serviceCodeDetailId === serviceCode.serviceCodeDetailId)) {
                this.serviceCodes[dateStr].push(serviceCode);
              }
            }
          });
        }
      });
    }
  }

  onSaveAdjustment(event: any) {
    this.dialogSvc.closeDialog(this.pendedDialogId);
    this.pendedDialogId = '';
    this.populateInvoiceData();
    this.invoiceUpdated.emit(true);
  }

  // Denied lines can be resubmitted for processing by internal staff only.
  get canReprocessDenied(): boolean {
    return this.staffView && !this.isExternal;
  }

  private isDeniedLine(entry: any): boolean {
    return entry.get('processStatusLkp')?.value === 'Denied';
  }

  onSubmitAdjudicationClick() {
    const selectedItems = this.invoiceEntries.controls.filter(entry => entry.get('selected')?.value);

    if (!selectedItems?.length) {
      const errorMessage = 'Please select lines for completing this action.';
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    const totalInvalidProcessSelected = selectedItems.filter((e: any) => e.get('processStatusLkp')?.value != 'Pended' && e.get('processStatusLkp')?.value != 'Submitted' && e.get('processStatusLkp')?.value != 'System Error' && !(this.canReprocessDenied && this.isDeniedLine(e)));
    if (totalInvalidProcessSelected.length > 0) {
      const errorMessage = this.canReprocessDenied
        ? 'Please select only Pending or Submitted or System Error or Denied invoices.'
        : 'Please select only Pending or Submitted or System Error invoices.';
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    const selectedIds = selectedItems.map(e => e.get('id')?.value);
    const payload =
    {
      "invoicelineDetailIds": selectedIds
    };

    this.invoiceService.submitAdjudicationDetails(payload, this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        this.populateInvoiceData();
        this.invoiceUpdated.emit(true);
      }
    });
  }

  onMoveBack() {
    const selectedItems = this.invoiceEntries.controls.filter(entry => entry.get('selected')?.value);
    const selectedIds = selectedItems.map(e => e.get('id')?.value);
    if (selectedIds.length === 0) {
      this.notificationService.error(
        'Error',
        `Please select at least one invoice line to moveBack.`
      );

      return;
    }

    const canMoveBack = !selectedItems.some((si: any) => si.get('processStatusLkp')?.value !== 'Awaiting Approval' && si.get('processStatusLkp')?.value !== 'Pended' && si.get('processStatusLkp')?.value !== 'Ready for Payment');
    if (!canMoveBack) {
      this.notificationService.error(
        'Error',
        `Only invoice lines with status 'Awaiting Approval', 'Pended', 'Ready for Payment' can be moved back.`
      );
      return;
    }

    const payload = {
      invoiceIds: selectedIds
    }

    this.invoiceService.postMoveBackLineItemsStatus(payload, this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        this.populateInvoiceData();
        this.invoiceUpdated.emit(true);
      }
    });
  } 

  onInActivateInvoiceLines(invoiceLines: any[] = []) {
    let selectedItems = this.invoiceEntries.controls.filter(entry => entry.get('selected')?.value);
    if (selectedItems.length === 0) {
      selectedItems = invoiceLines;
    }
    const selectedIds = selectedItems.map(e => e.get('id')?.value);
    if (selectedIds.length === 0) {
      this.notificationService.error(
        'Error',
        `Please select at least one invoice line to cancel.`
      );

      return;
    }

    const canCancel = !selectedItems.some((si: any) => si.get('processStatusLkp')?.value !== 'Awaiting Approval' && si.get('processStatusLkp')?.value !== 'Ready for Payment' && si.get('processStatusLkp')?.value !== 'Saved');
    if (!canCancel) {
      this.notificationService.error(
        'Error',
        `Only invoice lines with status 'Awaiting Approval', 'Ready for Payment' and 'Saved' can be cancelled.`
      );
      return;
    }

    const payload = {
      invoiceIds: selectedIds
    }

    this.invoiceService.postInactivateLines(payload, this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        this.populateInvoiceData();
        this.invoiceUpdated.emit(true);
      }
    });
  }

  canDisableUploadAttachments(): boolean {
    return this.mode === 'view' || this.mode === 'actions' || (this.mode === 'edit' && !this.canUpdateEntries);
  }

  showAuthDetails(event: any) {
    if (event.clientAuthNumber) {
      this.authDetails = event;
      this.openAuthDetailsDialog();
    }
  }

  openAuthDetailsDialog() {
    this.selectedAuthorizationId = this.authDetails.tasAuthorizationDetailId;
    const dialogConfig: DialogConfig = {
      title: `Auth Details for ${this.authDetails.clientAuthNumber}`,
      okText: 'Deny',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.authDialog,
      dialogConfig,
      () => this.handleAuthCancel(),
      () => this.handleAuthCancel()
    );
  }

  handleAuthCancel() {

  }

  openAuthUsageDataDialog() {
    const dialogConfig: DialogConfig = {
      title: `Authorizations`,
      okText: 'Deny',
      size: 'large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.authUsageDataDialog,
      dialogConfig,
      () => this.handleAuthUsageDataCancel(),
      () => this.handleAuthUsageDataCancel()
    );
  }

  handleAuthUsageDataCancel() {

  }

  onOpenAuthDataClicked(formControlIndex: number) {
    const formControl = this.invoiceEntries.at(formControlIndex);
    this.invoiceService
      .getAuthUsageData(this.programName, formControl.get('id')?.value)
      .subscribe((data: any) => {
        this.authUsageData = data.responsedata;
        this.openAuthUsageDataDialog();
      });
  }

  openInvoiceEditHistoryDialog(formControlIndex: number) {
    const formControl = this.invoiceEntries.at(formControlIndex);
    this.selectedInvoiceLineId = formControl?.get('id')?.value;
    const dialogConfig: DialogConfig = {
      title: `Invoice Edit History`,
      okText: 'Ok',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.invoiceEditHistoryDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openAuditHistoryDialog(formControlIndex: number) {
    const formControl = this.invoiceEntries.at(formControlIndex);
    this.selectedInvoiceLineId = formControl?.get('id')?.value;
    const dialogConfig: DialogConfig = {
      title: `Invoice Audit History`,
      okText: 'Ok',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'split-auth-data'
    };

    this.dialogSvc.openDialog(
      this.invoiceAuditHistoryDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  onPrint(): void {
    const printSection = document.getElementById('print-section');
    if (!printSection) return;

    const styleId = 'dynamic-print-style';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    // Mark all ancestors of #print-section so we can show them
    const ancestorClass = 'print-ancestor';
    let el: HTMLElement | null = printSection.parentElement;
    while (el && el !== document.body) {
      el.classList.add(ancestorClass);
      el = el.parentElement;
    }

    styleEl.innerHTML = `
    @media print {
 
      /* Hide all direct children of body */
      body > * {
        display: none !important;
      }
 
      /* Show the ancestor chain leading to #print-section */
      body > .print-ancestor,
      .print-ancestor > .print-ancestor,
      .print-ancestor {
        display: block !important;
        overflow: visible !important;
        background: white !important;
        position: static !important;
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        transform: none !important;
        max-width: none !important;
      }
 
      /* Hide siblings of ancestors */
      .print-ancestor > *:not(.print-ancestor):not(#print-section) {
        display: none !important;
      }
 
      /* Show print section */
      #print-section {
        display: block !important;
        visibility: visible !important;
        overflow: visible !important;
        position: static !important;
        width: 100% !important;
        height: auto !important;
        padding: 16px !important;
        background: white !important;
        box-sizing: border-box !important;
      }
 
      #print-section * {
        visibility: visible !important;
        overflow: visible !important;
      }
 
      /* Hide modal chrome */
      .ant-modal-mask,
      .ant-modal-close,
      .ant-modal-header,
      .ant-modal-footer {
        display: none !important;
      }
 
      /* Hide buttons and actions inside print section */
      #print-section ppl-button,
      #print-section button,
      #print-section .custom-btn-row,
      #print-section .action-container,
      #print-section input[type="checkbox"] {
        display: none !important;
      }
 
      /* Table — no scroll, full width */
      #print-section .table-wrapper {
        overflow: visible !important;
        width: 100% !important;
      }
 
      #print-section table {
        width: 100% !important;
        table-layout: auto !important;
        border-collapse: collapse !important;
        font-size: 11px !important;
      }
 
      #print-section th {
        background-color: #a8e4e14d !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        white-space: nowrap !important;
        padding: 5px 4px !important;
        border: 1px solid #ccc !important;
        font-size: 11px !important;
      }
 
      #print-section td {
        padding: 5px 4px !important;
        border: 1px solid #ccc !important;
        font-size: 11px !important;
        vertical-align: middle !important;
      }
 
      /* ✅ Actions column header - fully closed */
      #print-section th:last-child {
        border: 1px solid #ccc !important;
        background-color: #a8e4e14d !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        white-space: nowrap !important;
        width: 80px !important;
      }
 
      /* ✅ Actions column cell - fully closed, content hidden */
      #print-section td:last-child {
        display: table-cell !important;
        visibility: visible !important;
        border: 1px solid #ccc !important;
        width: 80px !important;
        height: 30px !important;
        padding: 5px 6px !important;
        overflow: hidden !important;
        background: white !important;
      }
 
      /* ✅ Hide everything inside Actions cell */
      #print-section td:last-child * {
        display: none !important;
        visibility: hidden !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
      }
 
      /* ✅ Ensure last row bottom border closes fully */
      #print-section tr:last-child td {
        border-bottom: 1px solid #ccc !important;
      }
 
      #print-section tr:last-child td:last-child {
        border-bottom: 1px solid #ccc !important;
        border-right: 1px solid #ccc !important;
      }
 
      /* nz-select — hide chrome, show value */
      #print-section .ant-select-arrow,
      #print-section .ant-select-clear,
      #print-section .ant-picker-suffix,
      #print-section .ant-picker-active-bar,
      #print-section .ant-select-dropdown {
        display: none !important;
      }
 
      #print-section .ant-select-selector {
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
 
      #print-section .ant-picker {
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
 
      #print-section input.ant-input,
      #print-section input[nz-input] {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
      }
 
      #print-section tr {
        page-break-inside: avoid !important;
      }
 
      @page {
        size: landscape;
        margin: 8mm 10mm;
      }
    }
  `;

    window.print();
    window.addEventListener('afterprint', () => {
      styleEl.innerHTML = '';
      document.querySelectorAll('.' + ancestorClass).forEach(node => {
        node.classList.remove(ancestorClass);
      });
    }, { once: true });
  }
}
